import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Offering {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  category?: string;
  image_url?: string;
  stock_quantity?: number;
}

export default function Storefront() {
  const [offerings, setOfferings] = useState<Offering[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [added, setAdded] = useState<Record<string, boolean>>({});

  useEffect(() => { loadOfferings(); }, [activeCategory, search]);

  async function loadOfferings() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.set('category', activeCategory);
      if (search) params.set('search', search);
      const res = await api.get(`/api/offerings?${params.toString()}`);
      setOfferings(res.data ?? []);
      const cats = [...new Set((res.data ?? []).map((o: Offering) => o.category).filter(Boolean))];
      setCategories(cats as string[]);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function addToCart(offeringId: string) {
    try {
      await api.post('/api/checkout/cart', { offering_id: offeringId, quantity: 1 });
      setAdded({ ...added, [offeringId]: true });
      setTimeout(() => setAdded((prev) => ({ ...prev, [offeringId]: false })), 2000);
    } catch (err: any) {
      alert(err.message || 'Failed to add to cart');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Storefront</h1>
        <a href="/#/checkout" style={{ fontSize: 13, color: '#111827', textDecoration: 'none', fontWeight: 500 }}>Go to Cart →</a>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveCategory('')} style={{ ...pill(activeCategory === '') }}>All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCategory(c)} style={{ ...pill(activeCategory === c) }}>{c}</button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search offerings..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 12px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 20, fontFamily: 'inherit', minWidth: 160, marginLeft: 'auto' }}
        />
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
          {offerings.map((o) => (
            <div key={o.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              {o.image_url ? (
                <img src={o.image_url} alt={o.name} style={{ width: '100%', height: 150, objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: 150, background: '#f3f4f6' }} />
              )}
              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 500, fontSize: 15 }}>{o.name}</div>
                {o.category && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{o.category}</div>}
                <p style={{ fontSize: 13, color: '#6b7280', margin: '6px 0', lineHeight: 1.4, minHeight: 36 }}>{o.description ?? ''}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <span style={{ fontWeight: 600 }}>{o.currency} {o.price.toFixed(2)}</span>
                  <button
                    onClick={() => addToCart(o.id)}
                    disabled={added[o.id]}
                    style={{
                      padding: '6px 14px',
                      fontSize: 13,
                      borderRadius: 8,
                      border: 'none',
                      background: added[o.id] ? '#166534' : '#111827',
                      color: '#fff',
                      cursor: added[o.id] ? 'default' : 'pointer',
                    }}
                  >
                    {added[o.id] ? 'Added ✓' : 'Add to Cart'}
                  </button>
                </div>
                {o.stock_quantity != null && (
                  <div style={{ fontSize: 11, color: o.stock_quantity > 0 ? '#6b7280' : '#dc2626', marginTop: 4 }}>
                    {o.stock_quantity > 0 ? `${o.stock_quantity} in stock` : 'Out of stock'}
                  </div>
                )}
              </div>
            </div>
          ))}
          {offerings.length === 0 && <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#9ca3af', padding: 60 }}>No offerings found.</div>}
        </div>
      )}
    </div>
  );
}

function pill(active: boolean): React.CSSProperties {
  return {
    padding: '4px 12px',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    background: active ? '#111827' : '#fff',
    color: active ? '#fff' : '#525252',
    fontSize: 12,
    cursor: 'pointer',
  };
}
